"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
require("isomorphic-unfetch");
const uuid_1 = require("uuid");
const EventParser_1 = require("./EventParser");
const RestProxyError_1 = require("./RestProxyError");
const AvroError_1 = require("./AvroError");
const EventFactory_1 = require("@videoblocks/events-ts/lib/util/EventFactory");
const FailedRecord_1 = require("@videoblocks/events-ts/lib/storyblocks/common/FailedRecord");
const btoa = require("btoa-lite");
class Client {
    constructor(restProxyUrl, origin, encode = true, referer) {
        this.origin = origin;
        this.encode = encode;
        this.referer = referer;
        this.restProxyUrl = this.cleanUrl(restProxyUrl);
        this.credentials = this.extractCredentials(restProxyUrl);
    }
    produce(record_1, key_1) {
        return __awaiter(this, arguments, void 0, function* (record, key, retries = 1) {
            const topic = EventParser_1.default.getTopic(record);
            const schema = EventParser_1.default.getSchema(record);
            try {
                const value = EventParser_1.default.parseValue(record);
                yield this.postSingle(topic, schema, value, key);
            }
            catch (err) {
                if (retries > 0 && err instanceof RestProxyError_1.default) {
                    yield this.produce(record, key, retries - 1);
                }
                else if (err instanceof AvroError_1.default || err instanceof RestProxyError_1.default) {
                    yield this.produceFailedRecord(record, err.message, key);
                }
                else {
                    throw err;
                }
            }
        });
    }
    /**
     * Keys are not currently supported for productMany(), mostly for convenience of client
     */
    produceMany(events_1) {
        return __awaiter(this, arguments, void 0, function* (events, retries = 1) {
            if (events.length < 1 || events.length > 1000) {
                throw new Error(`Client.productMany expects between 1-1000 events. ${events.length} events passed in.`);
            }
            const firstRecord = events[0];
            const topic = EventParser_1.default.getTopic(firstRecord);
            const schema = EventParser_1.default.getSchema(firstRecord);
            const validRecords = [];
            const invalidRecords = [];
            events.forEach((record) => {
                if (firstRecord.schema().name !== record.schema().name) {
                    throw new Error(`Client.produceMany expects all objects to be of the same type. Expected ${firstRecord.schema().name} got ${record.schema().name}`);
                }
                const key = (0, uuid_1.v4)();
                try {
                    const value = EventParser_1.default.parseValue(record);
                    validRecords.push({ key, value });
                }
                catch (err) {
                    const details = err instanceof Error ? err.message : "Unknown";
                    invalidRecords.push({ key, record, details });
                }
            });
            if (invalidRecords.length) {
                yield this.produceManyFailedRecords(invalidRecords);
            }
            if (!validRecords.length) {
                throw new Error("No valid records passed to produceMany");
            }
            try {
                yield this.post(topic, schema, validRecords);
            }
            catch (err) {
                if (retries > 0 && err instanceof RestProxyError_1.default && err.status !== 422) {
                    yield this.produceMany(events, retries - 1);
                }
                else {
                    throw err;
                }
            }
        });
    }
    getOrigin() {
        return this.origin;
    }
    produceFailedRecord(record, details, key) {
        return __awaiter(this, void 0, void 0, function* () {
            const failedRecord = EventFactory_1.EventFactory.failedRecord(record, EventParser_1.default.getTopic(record), this.origin, details);
            const topic = EventParser_1.default.getInvalidTopic(record);
            const schema = EventParser_1.default.getSchema(failedRecord);
            const value = EventParser_1.default.parseValue(failedRecord);
            yield this.postSingle(topic, schema, value, key);
        });
    }
    produceManyFailedRecords(records) {
        return __awaiter(this, void 0, void 0, function* () {
            const topic = EventParser_1.default.getInvalidTopic(records[0].record);
            const schema = JSON.stringify(FailedRecord_1.FailedRecord.schema);
            const failedRecords = records.map(({ key, record, details }) => {
                const failedRecord = EventFactory_1.EventFactory.failedRecord(record, EventParser_1.default.getTopic(record), this.origin, details);
                const value = EventParser_1.default.parseValue(failedRecord);
                return { key, value };
            });
            yield this.post(topic, schema, failedRecords);
        });
    }
    postSingle(topic, schema, value, key) {
        return __awaiter(this, void 0, void 0, function* () {
            const records = [
                {
                    key: key || (0, uuid_1.v4)(),
                    value,
                },
            ];
            return yield this.post(topic, schema, records);
        });
    }
    post(topic, schema, records) {
        return __awaiter(this, void 0, void 0, function* () {
            const uri = this.restProxyUrl + "/topics/" + topic;
            const headers = {};
            if (this.credentials) {
                // Note: This does not work for browsers that strip headers from pre-flight requests and servers which require Authorization for pre-flight (OPTIONS) requests.
                headers["Authorization"] = "Basic " + btoa(this.credentials.username + ":" + this.credentials.password);
            }
            if (this.referer) {
                headers["Referer"] = this.referer;
            }
            let body = JSON.stringify({
                key_schema: '{"type": "string"}',
                value_schema: schema,
                records: records,
            });
            if (this.encode) {
                headers["Content-Type"] = "text/plain";
                body = btoa(body);
            }
            else {
                headers["Content-Type"] = "application/vnd.kafka.avro.v2+json";
            }
            const response = yield fetch(uri, {
                method: "POST",
                headers,
                body,
            });
            if (response.status !== 200) {
                throw new RestProxyError_1.default(response);
            }
        });
    }
    cleanUrl(url) {
        if (!url.startsWith("http")) {
            url = "http://" + url.replace(/^\/+/, "");
        }
        // Remove basic auth if it exists.
        url = url.replace(/\w+?:\w+?@/, "");
        return url;
    }
    extractCredentials(url) {
        // Match groups: protocol, username, password, url
        const regex = /^(.+?\/\/)(.+?):(.+?)@(.+)$/;
        const matches = regex.exec(url);
        if (matches) {
            const [_, protocol, username, password] = matches;
            return { username, password };
        }
    }
}
exports.default = Client;
//# sourceMappingURL=Client.js.map